import { HttpParams } from '@angular/common/http';
import { format, sub } from 'date-fns';
import { compact, orderBy } from 'lodash-es';

import { constants } from '@shared/constants';
import { CustomHttpParamEncoder } from '@shared/encoder';
import {
  RequestPageParams,
  TransactionDetails,
  TransactionDetailsRaw,
  TransactionFinancialAccount,
  TransactionHistoryList,
  TransactionHistoryListResponse,
  TransactionList,
  TransactionListItem,
  TransactionListRaw,
  TransactionListRequest,
} from '@shared/models';
import { filterAmountMinMax, filterCreatedAtDateRange, systemTimeZone, toTitleCase } from '@shared/utils';
import { getMaskedAccountNumber, getSolutionDisplayName, getSubtypeDisplayName } from '@shared/utils';
import { formatDate } from '@angular/common';

/**
 * Returns the HttpParams object for making transactions API request with provided request parameters.
 * @param {TransactionListRequest} requestParams - The request parameters object containing pagination, sorting, filtering and batchId.
 * @returns {HttpParams} - The HttpParams object containing the query parameters to be sent in the request.
 */
export const transactionsRequestParams = (requestParams: TransactionListRequest): HttpParams => {
  const { page, size, searchString, sortParams, activeFilters, businessAccountId } = requestParams;
  let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

  params = params.append('size', size ?? constants.TABLE_ROWS);

  params = params.append('page', page ?? 0);

  if (businessAccountId) {
    params = params.append('businessAccountId', businessAccountId);
  }

  if (searchString) {
    params = params.append('keyword', searchString);
  }

  if (sortParams) {
    params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
  }

  if (activeFilters) {
    Object.keys(activeFilters).forEach((key) => {
      if (key === 'date') {
        const defaultRange = {
          from: format(sub(new Date(), { days: 30 }), 'yyyy-MM-dd'),
          to: format(new Date(), 'yyyy-MM-dd'),
        };
        const { from, to } = filterCreatedAtDateRange(activeFilters[key], defaultRange) ?? {};

        if (from && to) {
          params = params.append('createdDateFrom', from);
          params = params.append('createdDateTo', to);
          params = params.append('timeZone', systemTimeZone);
        }
      } else if (key === 'amount') {
        const { min, max } = filterAmountMinMax(activeFilters[key]);

        if (min && max) {
          params = params.append('amountFrom', min);
          params = params.append('amountTo', max);
        }
      } else {
        params = params.append(key, activeFilters[key]!);
      }
    });
  }

  return params;
};

export const mapTransactionListData = (transactionsData?: TransactionListRaw): TransactionList => {
  const { content = [], totalElements = 0, hasNext = false } = transactionsData ?? {};

  const items: TransactionListItem[] = content.map((item) => {
    const bankNameMoveFrom = item.debitFinancialAccount?.bankAccount?.bankName;
    const bankNameMoveTo = item.creditFinancialAccount?.bankAccount?.bankName;
    const nameOnCardMoveTo = item.creditFinancialAccount?.card
      ? [item.creditFinancialAccount.card.firstName, item.creditFinancialAccount.card.lastName].join(' ')
      : '';

    const moveFromAccount = compact([
      getMaskedAccountNumber(item.maskedDebitAccountNumber),
      getSubtypeDisplayName(item.debitFinancialAccount?.subtype),
    ]).join(' | ');

    const moveToAccount = compact([
      getMaskedAccountNumber(item.maskedCreditAccountNumber),
      getSubtypeDisplayName(item.creditFinancialAccount?.subtype),
    ]).join(' | ');

    return {
      id: item.id,
      currency: item.currency,
      moveFrom: `${item.debitFinancialAccount?.name ?? ''}::${compact([bankNameMoveFrom, moveFromAccount]).join(' ')}`,
      moveTo: `${item.creditFinancialAccount?.name ?? ''}::${compact([bankNameMoveTo ?? nameOnCardMoveTo, moveToAccount]).join(' ')}`,
      solutionName: getSolutionDisplayName(item.solution),
      solution: item.solution,
      status: item.latestStatus?.status,
      amount: item.amount,
      createdAt: item.createdAt,
      transactionType: toTitleCase(item.transactionType),
      paymentReason: item.paymentReason,
    };
  });

  return {
    items,
    totalElements,
    hasNext,
  };
};

export const mapTransactionDetails = (response: TransactionDetailsRaw): TransactionDetails => {
  const { metadata = {}, debitFinancialAccount, creditFinancialAccount, latestStatus } = response;

  return {
    ...response,
    status: latestStatus.status === 'DELETED' ? 'CANCELLED' : latestStatus.status,
    debitFinancialAccount: mapFinancialAccountInfo(debitFinancialAccount),
    creditFinancialAccount: mapFinancialAccountInfo(creditFinancialAccount),
    metadataItems: Object.entries(metadata).map(([key, value]) => ({ key, value })),
  };
};

export const mapFinancialAccountInfo = (financialAccount: TransactionFinancialAccount): TransactionFinancialAccount => {
  if (!financialAccount) {
    return financialAccount;
  }
  const { maskedAccountNumber, bankAccount, name, card, subtype } = financialAccount;
  const bankName = bankAccount?.bankName;
  const nameOnCard = compact([card?.firstName, card?.lastName]).join(' ');
  const accNo = compact([getMaskedAccountNumber(maskedAccountNumber), getSubtypeDisplayName(subtype)]).join(' | ');
  const accountInfo = `${name ?? ''}::${compact([bankName ?? nameOnCard, accNo]).join(' ')}`;
  const address = card?.address ?? bankAccount?.address;

  return {
    ...financialAccount,
    accountInfo,
    address,
  };
};

export const transactionJournalActivitiesRequestParams = (requestParams: RequestPageParams): HttpParams => {
  const { page, size, activeFilters } = requestParams;

  const pageSize = size ?? constants.TABLE_ROWS;

  let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

  params = params.append('size', pageSize);

  if (page) {
    params = params.append('page', page);
  }

  if (activeFilters) {
    Object.keys(activeFilters).forEach((key) => {
      if (key === 'date') {
        const defaultRange = {
          from: format(sub(new Date(), { days: 90 }), 'yyyy-MM-dd'),
          to: format(new Date(), 'yyyy-MM-dd'),
        };
        const { from, to } = filterCreatedAtDateRange(activeFilters[key], defaultRange) ?? {};

        if (!from || !to) {
          return;
        }

        params = params.append('startDateTime', formatDate(from, 'yyyy-MM-ddTHH:mm:ssZZZZZ', 'en'));
        params = params.append('endDateTime', formatDate(`${to} 23:59:59`, 'yyyy-MM-ddTHH:mm:ssZZZZZ', 'en'));
        params = params.append('timeZone', systemTimeZone);
      } else if (key === 'amount') {
        const [min, max] = activeFilters[key]?.split('::') ?? [];

        if (!min || !max) {
          return;
        }

        params = params.append('amountFrom', min);
        params = params.append('amountTo', max);
      } else {
        params = params.append(key, activeFilters[key]!);
      }
    });
  }

  return params;
};

export const mapTransactionHistoryData = (response: TransactionHistoryListResponse): TransactionHistoryList => {
  const descriptionMessages: Record<string, string> = {
    'Transaction status changed to ‘CANCELLED’': 'red',
    'Transaction status changed to ‘CANCELED’': 'red',
    'Transaction status changed to ‘ERROR’': 'red',
    'Transaction status changed to ‘DECLINED’': 'red',
    'Request declined due to': 'red',
    'New transaction created': 'orange',
    'Transaction status changed to ‘PROCESSING’': 'orange',
    'Transaction status changed to ‘NEW’': 'orange',
    'Transaction status changed to ‘PENDING’': 'orange',
    'Transaction status changed to ‘SETTLED’': 'green',
    'Transaction status changed to ‘CLEARED’': 'green',
    'Transaction status changed to ‘APPROVED’': 'green',
  };

  const { content = [], totalElements = 0, hasNext = false, number = 0 } = response || {};
  const items = orderBy(
    content.map((item) => {
      const splittedMessage = item.description.split(':');
      return {
        ...item,
        status: descriptionMessages[item.description] || descriptionMessages[splittedMessage[0]] || 'orange',
      };
    }),
    'createdAt',
    'desc',
  );

  return {
    items,
    totalElements,
    hasNext,
    number,
  };
};
